import { useQueryClient } from '@tanstack/react-query';
import { AxiosPromise } from 'axios';
import { useCallback } from 'react';

import { settlementChargesClient } from 'app/api/apiClient/settlement/settlementChargesClient';
import { BillsBillChargeCreatePayload, BillsBillResponse } from 'app/api/apiClient/settlement/types';
import { queries } from 'app/constants/queries';
import { useMutationEnhanced } from 'app/hooks/useMutationEnhanced';
import { useAlertsDispatch } from 'app/state/AlertsContext/hooks';
import { removeItemFromArray } from 'app/utils/lib/array';
import { handleSuccess } from 'app/utils/lib/context';

export const useCreateCharge = () => {
  const queryClient = useQueryClient();

  return useMutationEnhanced(settlementChargesClient.createCharge,
    {
      onSuccess: data => {
        const billData = queryClient.getQueryData<BillsBillResponse>([queries.billsShow, data.charge.billId]);

        if (billData) {
          queryClient.setQueryData<BillsBillResponse>([queries.billsShow, data.charge.billId],
            {
              bill: {
                ...billData.bill,
                charges: [
                  ...billData.bill.charges,
                  data.charge,
                ],
              },
            });
        }
      },
    });
};

export const useDeleteCharge = (billId: string | undefined) => {
  const queryClient = useQueryClient();

  const alertsDispatch = useAlertsDispatch();

  return useMutationEnhanced(settlementChargesClient.deleteCharge,
    {
      onSuccess: (_data, chargeId) => {
        const billData = queryClient.getQueryData<BillsBillResponse>([queries.billsShow, billId]);

        if (billData) {
          const deletedChargeIndex = billData.bill.charges.findIndex(charge => charge.id === chargeId);

          const newCharges = removeItemFromArray(billData.bill.charges, deletedChargeIndex);

          queryClient.setQueryData<BillsBillResponse>([queries.billsShow, billId],
            {
              bill: {
                ...billData.bill,
                charges: newCharges,
              },
            });
        } else {
          void queryClient.invalidateQueries([queries.billsShow, billId]);
        }

        handleSuccess({
          dispatch: alertsDispatch,
          successMessage: 'Accessorial deleted!',
        });
      },
    });
};

export const useCreateOrUpdateCharge = () => {
  const queryClient = useQueryClient();

  const mutationFn = useCallback(async ({
    amount,
    billId,
    chargeTypeId,
  }: {
    amount: BillsBillChargeCreatePayload['charge']['amount'] | undefined;
    billId: BillsBillChargeCreatePayload['charge']['billId'];
    chargeTypeId: BillsBillChargeCreatePayload['charge']['chargeTypeId'];
  }) => {
    const initialBillData = queryClient.getQueryData<BillsBillResponse>([queries.billsShow, billId]);

    const existingCharge = initialBillData?.bill.charges.find(charge => charge.chargeTypeId === chargeTypeId);

    const chargeChanged = amount !== undefined && existingCharge &&
      existingCharge.amount !== parseFloat(`${amount}`).toFixed(2);

    if (amount === undefined) {
      return { data: null } as unknown as AxiosPromise<null>;
    }

    const createChargePayload: BillsBillChargeCreatePayload = {
      charge: {
        amount,
        billId,
        chargeTypeId,
      },
    };

    if (!existingCharge) {
      return settlementChargesClient.createCharge(createChargePayload);
    }

    if (existingCharge && chargeChanged) {
      await settlementChargesClient.deleteCharge(existingCharge.id);

      return settlementChargesClient.createCharge(createChargePayload);
    }

    return { data: null } as unknown as AxiosPromise<null>;
  },
  [queryClient]);

  return useMutationEnhanced(mutationFn,
    {
      onSuccess: data => {
        const charge = data?.charge;

        if (charge) {
          const billId = charge.billId;

          const billData = queryClient.getQueryData<BillsBillResponse>([queries.billsShow, billId]);

          const bill = billData?.bill;

          if (!bill) {
            return;
          }

          const charges = bill.charges.length === 0
            ? [charge]
            : bill.charges.map(c => {
              if (c.chargeTypeId === charge.chargeTypeId) {
                return charge;
              }

              return c;
            });

          queryClient.setQueryData<BillsBillResponse>([queries.billsShow, billId],
            {
              bill: {
                ...bill,
                charges,
              },
            });
        }
      },
    });
};
